// React error boundary that emits `error.boundary` then renders the parent's
// fallback (if provided) or null. The fallback shape is owned by whoever
// mounts the boundary — we don't introduce a new error screen here.

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { track } from '../lib/telemetry';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
};

export class TelemetryErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    track('error.boundary', {
      message: error.message.slice(0, 500),
      stack: error.stack?.slice(0, 1000) ?? null,
      componentStack: info.componentStack?.slice(0, 1000) ?? null,
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
